import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import NotificationIcon from '../notifications/NotificationIcon';

const TopBarRight = () => {
  const navigate = useNavigate();
  const { isDarkMode, toggleTheme } = useTheme();
  const { isAuthenticated, user, logout, isLoading } = useAuth();
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
    setShowDropdown(false);
  };

  return (
    <div className="navbar-actions">
      {isAuthenticated && <NotificationIcon />}
      <button
        type="button"
        className="btn icon theme-toggle"
        onClick={toggleTheme}
        aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
        title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
      >
        {isDarkMode ? (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="5"></circle>
            <line x1="12" y1="1" x2="12" y2="3"></line>
            <line x1="12" y1="21" x2="12" y2="23"></line>
            <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
            <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
            <line x1="1" y1="12" x2="3" y2="12"></line>
            <line x1="21" y1="12" x2="23" y2="12"></line>
            <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
            <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
          </svg>
        ) : (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
          </svg>
        )}
      </button>

      {isLoading ? null : isAuthenticated ? (
        <div className="navbar-user" ref={dropdownRef}>
          <div className="user-link-wrapper">
            <Link
              to={`/u/${user?.username}`}
              className="user-link-name"
              title="My profile"
            >
              {user?.username || 'User'}
            </Link>
            <button
              type="button"
              className="user-link-arrow"
              onClick={() => setShowDropdown(!showDropdown)}
              title="User menu"
              aria-label="User menu"
              aria-expanded={showDropdown}
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                style={{ transition: 'transform 0.2s', transform: showDropdown ? 'rotate(180deg)' : 'rotate(0deg)' }}
              >
                <path d="M3 4.5L6 7.5L9 4.5" />
              </svg>
            </button>
          </div>

          {showDropdown && (
            <div className="user-dropdown">
              <Link
                to={`/u/${user?.username}`}
                className="user-dropdown-item"
                onClick={() => setShowDropdown(false)}
              >
                <span className="dropdown-icon">👤</span>
                Profile
              </Link>
              <Link
                to="/teams/my-teams"
                className="user-dropdown-item"
                onClick={() => setShowDropdown(false)}
              >
                <span className="dropdown-icon">👥</span>
                My Teams
              </Link>
              <Link
                to={`/u/${user?.username}#pending-reviews`}
                className="user-dropdown-item"
                onClick={() => setShowDropdown(false)}
              >
                <span className="dropdown-icon">⏳</span>
                Pending Reviews
              </Link>
              <Link
                to="/settings"
                className="user-dropdown-item"
                onClick={() => setShowDropdown(false)}
              >
                <span className="dropdown-icon">⚙️</span>
                Settings
              </Link>
              <div className="user-dropdown-divider"></div>
              <button
                onClick={handleLogout}
                className="user-dropdown-item user-dropdown-item-danger"
              >
                <span className="dropdown-icon">🚪</span>
                Sign Out
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="navbar-auth">
          <Link to="/login" className="btn btn-sm btn-secondary">Login</Link>
          <Link to="/signup" className="btn btn-sm btn-primary">Sign Up</Link>
        </div>
      )}
    </div>
  );
};

export default TopBarRight;
